import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import TutorialHelp from "@/components/TutorialHelp";

const TYPES = [
  { value: "topic_selection", label: "Topic Selection", desc: "Refine and validate your research topic" },
  { value: "literature_review", label: "Literature Review", desc: "Search strategy, synthesis, gap analysis" },
  { value: "methodology_design", label: "Methodology Design", desc: "Research questions, method selection, protocol" },
  { value: "methodology_refinement", label: "Methodology Refinement", desc: "Iterate and strengthen your method" },
  { value: "methodology_implementation", label: "Methodology Implementation", desc: "Help executing your research plan" },
  { value: "statistical_analysis", label: "Statistical Analysis", desc: "SPSS, R, Python, PLS-SEM workflows" },
  { value: "technical_implementation", label: "Technical Implementation", desc: "Code, ML pipelines, experiments" },
  { value: "publication_preparation", label: "Publication Preparation", desc: "Manuscript, cover letter, submission" },
];

export default function GuidanceNew() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const preselectedMentorId = search.get("mentor_id") || "";
  const [form, setForm] = useState({
    type: "topic_selection", problem_description: "", estimated_hours: 5, files: [],
    domain: "", preferred_mentor_id: preselectedMentorId, proposed_slots: [""],
  });
  const [submitting, setSubmitting] = useState(false);
  const [domains, setDomains] = useState([]);
  const [mentors, setMentors] = useState([]);

  useEffect(() => {
    api.get("/domains").then((r) => setDomains(r.data?.domains || [])).catch(() => setDomains([]));
    api.get("/mentors").then((r) => setMentors(r.data || [])).catch(() => setMentors([]));
  }, []);

  const filteredMentors = form.domain
    ? mentors.filter((m) => (m.domains || []).includes(form.domain) || (m.expertise || []).some((e) => (e || "").toLowerCase().includes(form.domain.toLowerCase())))
    : mentors;
  const pickedMentor = mentors.find((m) => m.id === form.preferred_mentor_id) || null;
  const autoFee = pickedMentor?.hourly_rate_npr && form.estimated_hours
    ? Math.round(Number(pickedMentor.hourly_rate_npr) * Number(form.estimated_hours) * 100) / 100
    : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.domain) { toast.error("Pick a research domain so we can match the right mentor"); return; }
    if (form.problem_description.length < 30) { toast.error("Add more detail to your problem description"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        preferred_mentor_id: form.preferred_mentor_id || null,
        proposed_slots: form.proposed_slots.filter((s) => (s || "").trim().length > 0),
      };
      await api.post("/guidance", payload);
      toast.success(form.preferred_mentor_id
        ? (autoFee != null ? `Request sent — auto-quoted NPR ${autoFee}. Awaiting mentor to confirm slot.` : "Request sent — awaiting admin pricing.")
        : "Request sent — admin will recommend a mentor.");
      navigate("/dashboard/guidance");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="guidance-new-page" className="max-w-3xl mx-auto">
      <Link to="/dashboard/guidance" className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2">
        <ArrowLeft className="w-3.5 h-3.5" /> BACK
      </Link>
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mt-6">NEW GUIDANCE REQUEST</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">What do you need <em className="italic text-kb-primary">help with</em>?</h1>

      <div className="mt-6">
        <TutorialHelp tutorialKey="guidance-request" />
      </div>

      <form onSubmit={submit} className="mt-10 space-y-6" data-testid="guidance-new-form">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-3">TYPE OF SUPPORT</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {TYPES.map((t) => (
              <button type="button" key={t.value} onClick={() => setForm({ ...form, type: t.value })}
                className={`text-left border rounded-sm p-3 transition-colors ${form.type === t.value ? "border-kb-primary bg-kb-primary/5" : "border-slate-200 hover:border-kb-primary/50"}`}
                data-testid={`guidance-type-${t.value}`}>
                <div className="text-sm font-medium text-kb-ink">{t.label}</div>
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1 line-clamp-2">{t.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="kb-card p-6 space-y-4">
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Research domain *</label>
            <select
              required
              className="kb-input mt-1.5"
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
              data-testid="guidance-domain-select"
            >
              <option value="">— Select your research domain —</option>
              {domains.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <div className="kb-mono text-[10px] text-kb-ink-light mt-1">We&apos;ll match you with a mentor whose expertise overlaps this domain.</div>
          </div>

          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Describe your problem *</label>
            <textarea rows={6} required className="kb-input mt-1.5 resize-none" value={form.problem_description}
              onChange={(e) => setForm({ ...form, problem_description: e.target.value })}
              placeholder="What are you stuck on? What have you tried? What outcome do you need?"
              data-testid="guidance-description-input" />
            <div className="kb-mono text-[10px] text-kb-ink-light mt-1">{form.problem_description.length} characters</div>
          </div>

          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Estimated hours needed</label>
            <input type="number" min="1" max="100" step="0.5" className="kb-input mt-1.5 max-w-[180px]"
              value={form.estimated_hours} onChange={(e) => setForm({ ...form, estimated_hours: (e.target.value === "" ? "" : (parseFloat(e.target.value) || 1)) })}
              data-testid="guidance-hours-input" />
            <div className="kb-mono text-[10px] text-kb-ink-light mt-1">Fee auto-calculates from the picked mentor&apos;s hourly rate.</div>
          </div>

          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Preferred mentor (optional)</label>
            <select className="kb-input mt-1.5" value={form.preferred_mentor_id}
              onChange={(e) => setForm({ ...form, preferred_mentor_id: e.target.value })}
              data-testid="guidance-mentor-select">
              <option value="">— Let admin recommend a mentor —</option>
              {filteredMentors.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.hourly_rate_npr ? ` · NPR ${m.hourly_rate_npr}/hr` : " · rate TBD"}{m.affiliation ? ` · ${m.affiliation}` : ""}
                </option>
              ))}
            </select>
            {pickedMentor && (
              <div className="mt-2 text-xs text-kb-ink-muted bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <div><strong>{pickedMentor.name}</strong>{pickedMentor.title ? ` · ${pickedMentor.title}` : ""}</div>
                <div className="kb-mono text-[10px] text-kb-ink-light">{(pickedMentor.expertise || []).slice(0, 5).join(" · ")}</div>
                {autoFee != null && (
                  <div className="mt-1 text-emerald-700 font-medium">Auto-quote: NPR {autoFee.toLocaleString()} ({form.estimated_hours}h × NPR {pickedMentor.hourly_rate_npr}/hr)</div>
                )}
                {autoFee == null && <div className="mt-1 text-amber-700">This mentor has no hourly rate yet — admin will price manually.</div>}
              </div>
            )}
            {!pickedMentor && (
              <div className="kb-mono text-[10px] text-kb-ink-light mt-1">Not sure who? Leave blank and our admin will match you to the best fit.</div>
            )}
          </div>

          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Propose preferred meeting slots (optional)</label>
            <div className="mt-1.5 space-y-1.5">
              {form.proposed_slots.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <input type="datetime-local" className="kb-input flex-1" value={s}
                    onChange={(e) => setForm({ ...form, proposed_slots: form.proposed_slots.map((x, j) => j === i ? e.target.value : x) })}
                    data-testid={`guidance-slot-${i}`} />
                  {form.proposed_slots.length > 1 && (
                    <button type="button" onClick={() => setForm({ ...form, proposed_slots: form.proposed_slots.filter((_, j) => j !== i) })} className="kb-btn-outline text-xs px-2">✕</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setForm({ ...form, proposed_slots: [...form.proposed_slots, ""] })} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary hover:underline">
                + Add another slot
              </button>
            </div>
            <div className="kb-mono text-[10px] text-kb-ink-light mt-1">The mentor accepts one or counter-proposes. Payment unlocks after both agree.</div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={submitting} className="kb-btn-primary" data-testid="guidance-submit-btn">
            {submitting ? "Submitting…" : (<>Submit request <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <Link to="/dashboard/guidance" className="kb-btn-outline">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
